/*
  NUDEBUM — JS global
  Custom elements sin dependencias. Todo es mejora progresiva: sin JS, los enlaces
  y formularios siguen funcionando (carrito, búsqueda y producto envían a sus páginas).
*/
(() => {
  const nb = window.nb || {};
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isDesktop = window.matchMedia('(min-width: 990px)');

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

  /* ---------------------------------------------------------
     Drawers (menú, búsqueda, carrito)
     --------------------------------------------------------- */
  let openDrawer = null;

  class NbDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.nb-drawer__panel');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trapFocus(e);
      });
    }

    open(trigger) {
      if (openDrawer && openDrawer !== this) openDrawer.close({ restoreFocus: false });
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('nb-scroll-locked');
      $$(`[aria-controls="${this.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'true'));
      openDrawer = this;
      const focusTarget = this.querySelector('[data-search-input]') || this.panel;
      requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
      this.dispatchEvent(new CustomEvent('nb:drawer-open', { bubbles: true }));
    }

    close({ restoreFocus = true } = {}) {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('nb-scroll-locked');
      $$(`[aria-controls="${this.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'false'));
      if (openDrawer === this) openDrawer = null;
      if (restoreFocus && this.trigger && this.trigger.focus) this.trigger.focus({ preventScroll: true });
    }

    trapFocus(e) {
      const items = $$(FOCUSABLE, this.panel).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  customElements.define('nb-drawer', NbDrawer);

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.dataset.drawerOpen);
    if (!drawer) return;
    // En la página de carrito, el ícono de bolsa navega normal.
    if (trigger.dataset.drawerOpen === 'nb-cart-drawer' && document.body.classList.contains('nb-template-cart')) return;
    e.preventDefault();
    drawer.open(trigger);
  });

  /* ---------------------------------------------------------
     Header: estado sólido al hacer scroll + mega menú
     --------------------------------------------------------- */
  class NbHeader extends HTMLElement {
    connectedCallback() {
      this.overlay = this.dataset.overlay === 'true';
      if (this.overlay) {
        let ticking = false;
        const onScroll = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            this.classList.toggle('is-solid', window.scrollY > 24);
            ticking = false;
          });
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        this.addEventListener('mouseenter', () => this.classList.add('is-solid'));
        this.addEventListener('mouseleave', () => {
          if (window.scrollY <= 24 && !this.querySelector('details[open]')) this.classList.remove('is-solid');
        });
        this.addEventListener('focusin', () => this.classList.add('is-solid'));
      }

      this.megas = $$('[data-mega]', this);
      this.megas.forEach((details) => {
        let timer;
        const item = details.closest('.nb-nav__item');
        item.addEventListener('mouseenter', () => {
          if (!isDesktop.matches) return;
          clearTimeout(timer);
          timer = setTimeout(() => this.openMega(details), 90);
        });
        item.addEventListener('mouseleave', () => {
          clearTimeout(timer);
          timer = setTimeout(() => details.removeAttribute('open'), 160);
        });
        details.addEventListener('toggle', () => {
          if (details.open) this.megas.forEach((d) => d !== details && d.removeAttribute('open'));
          if (this.overlay) this.classList.toggle('is-solid', details.open || window.scrollY > 24);
        });
        details.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && details.open) {
            details.removeAttribute('open');
            details.querySelector('summary').focus();
          }
        });
      });

      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.megas.forEach((d) => d.removeAttribute('open'));
      });
    }

    openMega(details) {
      this.megas.forEach((d) => d !== details && d.removeAttribute('open'));
      details.setAttribute('open', '');
    }
  }
  customElements.define('nb-header', NbHeader);

  /* ---------------------------------------------------------
     Barra de anuncios (rotación en mobile)
     --------------------------------------------------------- */
  class NbRotator extends HTMLElement {
    connectedCallback() {
      this.items = $$('.nb-announce__item', this);
      if (this.items.length < 2) return;
      this.index = 0;
      this.paused = false;
      ['mouseenter', 'focusin'].forEach((ev) => this.addEventListener(ev, () => (this.paused = true)));
      ['mouseleave', 'focusout'].forEach((ev) => this.addEventListener(ev, () => (this.paused = false)));
      this.timer = setInterval(() => this.next(), Number(this.dataset.interval) || 4500);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    next() {
      if (this.paused || isDesktop.matches || document.hidden) return;
      const current = this.items[this.index];
      this.index = (this.index + 1) % this.items.length;
      const upcoming = this.items[this.index];
      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      upcoming.classList.add('is-active');
      setTimeout(() => current.classList.remove('is-leaving'), 520);
    }
  }
  customElements.define('nb-rotator', NbRotator);

  /* ---------------------------------------------------------
     Carrito (AJAX + Section Rendering API)
     --------------------------------------------------------- */
  const CART_SECTION = 'nb-cart-drawer';

  const cart = {
    async request(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: [CART_SECTION], sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        const message = data.description || data.message || (nb.strings && nb.strings.cartError);
        throw new Error(message);
      }
      return data;
    },

    async add(items) {
      const data = await this.request(`${nb.routes.cartAdd}.js`, { items });
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('nb:cart-updated', { detail: data }));
      const drawer = document.getElementById(CART_SECTION);
      if (drawer && !document.body.classList.contains('nb-template-cart')) drawer.open();
      return data;
    },

    async change(line, quantity) {
      const panel = $('[data-cart-panel]');
      panel && panel.classList.add('is-updating');
      try {
        const data = await this.request(`${nb.routes.cartChange}.js`, { line, quantity });
        this.render(data.sections);
        document.dispatchEvent(new CustomEvent('nb:cart-updated', { detail: data }));
        if (document.body.classList.contains('nb-template-cart')) window.location.reload();
      } catch (err) {
        toast(err.message);
      } finally {
        const p = $('[data-cart-panel]');
        p && p.classList.remove('is-updating');
      }
    },

    render(sections) {
      if (!sections || !sections[CART_SECTION]) return;
      const doc = new DOMParser().parseFromString(sections[CART_SECTION], 'text/html');
      const fresh = $('[data-cart-panel]', doc);
      const current = $('[data-cart-panel]');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
      const countEl = $('[data-cart-count-value]', doc);
      if (countEl) updateCount(Number(countEl.dataset.cartCountValue));
    },
  };
  nb.cart = cart;

  function updateCount(count) {
    $$('[data-cart-count]').forEach((badge) => {
      badge.hidden = count === 0;
      const visible = badge.querySelector('[aria-hidden]');
      if (visible) visible.textContent = count;
      const label = badge.querySelector('.nb-visually-hidden');
      if (label) label.textContent = label.textContent.replace(/\d+/, count);
      badge.classList.remove('is-bumped');
      void badge.offsetWidth;
      badge.classList.add('is-bumped');
    });
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-cart-change]');
    if (!btn) return;
    e.preventDefault();
    cart.change(Number(btn.dataset.cartChange), Number(btn.dataset.quantity));
  });

  let toastTimer;
  function toast(message) {
    const el = $('[data-toast]');
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (el.hidden = true), 4200);
  }
  nb.toast = toast;

  /* ---------------------------------------------------------
     Formulario de producto (PDP y quick add)
     --------------------------------------------------------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const error = form.querySelector('[data-form-error]');
    const id = form.querySelector('[name="id"]');
    if (!id || !id.value) {
      // La product page muestra su propio mensaje junto a las tallas.
      if (error && !form.querySelector('[data-size-picker]')) { error.textContent = nb.strings.chooseSize; error.hidden = false; }
      form.dispatchEvent(new CustomEvent('nb:missing-option', { bubbles: true }));
      return;
    }
    const quantity = Number((form.querySelector('[name="quantity"]') || {}).value || 1);
    button && button.classList.add('is-loading');
    if (error) error.hidden = true;
    try {
      await cart.add([{ id: Number(id.value), quantity }]);
    } catch (err) {
      if (error) { error.textContent = err.message; error.hidden = false; } else toast(err.message);
    } finally {
      button && button.classList.remove('is-loading');
    }
  });

  /* ---------------------------------------------------------
     Completa el look
     --------------------------------------------------------- */
  class NbLook extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-look-add]');
      this.error = this.querySelector('[data-look-error]');
      this.button && this.button.addEventListener('click', () => this.addAll());
      this.addEventListener('change', (e) => {
        const item = e.target.closest('[data-look-item]');
        item && item.classList.remove('is-missing');
        if (this.error) this.error.hidden = true;
      });
    }

    async addAll() {
      const items = [];
      const missing = [];
      $$('[data-look-item]', this).forEach((item) => {
        const fixed = item.querySelector('[data-look-fixed]');
        const picked = item.querySelector('[data-look-size]:checked');
        if (fixed) items.push({ id: Number(fixed.value), quantity: 1 });
        else if (picked) items.push({ id: Number(picked.value), quantity: 1 });
        else missing.push(item);
      });
      if (missing.length) {
        missing.forEach((item) => {
          item.classList.remove('is-missing');
          void item.offsetWidth;
          item.classList.add('is-missing');
        });
        if (this.error) {
          const names = missing.map((m) => m.dataset.title).join(', ');
          this.error.textContent = `${nb.strings.chooseSize}: ${names}`;
          this.error.hidden = false;
        }
        const firstInput = missing[0].querySelector('input:not([disabled])');
        firstInput && firstInput.focus();
        return;
      }
      this.button.classList.add('is-loading');
      try {
        await cart.add(items);
      } catch (err) {
        if (this.error) { this.error.textContent = err.message; this.error.hidden = false; }
      } finally {
        this.button.classList.remove('is-loading');
      }
    }
  }
  customElements.define('nb-look', NbLook);

  /* ---------------------------------------------------------
     Búsqueda predictiva
     --------------------------------------------------------- */
  class NbPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('[data-search-input]', this);
      this.results = $('[data-search-results]', this);
      this.defaults = $('[data-search-default]', this);
      this.cache = new Map();
      this.input.addEventListener('input', debounce(() => this.search(), 220));
      this.input.addEventListener('keydown', (e) => this.onKey(e));
    }

    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.results.innerHTML = '';
        this.defaults.hidden = false;
        this.input.setAttribute('aria-expanded', 'false');
        return;
      }
      const key = q.toLowerCase();
      let html = this.cache.get(key);
      if (!html) {
        this.controller && this.controller.abort();
        this.controller = new AbortController();
        const params = new URLSearchParams({
          q,
          'resources[type]': 'product,collection,page',
          'resources[limit]': '6',
          'resources[options][fields]': 'title,product_type,variants.title,tag',
          section_id: 'nb-predictive-search',
        });
        try {
          const res = await fetch(`${nb.routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
          const text = await res.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const el = $('.nb-predictive', doc);
          html = el ? el.outerHTML : '';
          this.cache.set(key, html);
        } catch (err) {
          if (err.name !== 'AbortError') console.error(err);
          return;
        }
      }
      this.results.innerHTML = html;
      this.defaults.hidden = true;
      this.input.setAttribute('aria-expanded', 'true');
      this.active = -1;
    }

    onKey(e) {
      const options = $$('[role="option"]', this.results);
      if (!options.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      if (e.key === 'Enter') {
        if (this.active >= 0) {
          e.preventDefault();
          options[this.active].querySelector('a').click();
        }
        return;
      }
      e.preventDefault();
      this.active = e.key === 'ArrowDown' ? Math.min(this.active + 1, options.length - 1) : Math.max(this.active - 1, -1);
      options.forEach((o, i) => o.setAttribute('aria-selected', String(i === this.active)));
      if (this.active >= 0) this.input.setAttribute('aria-activedescendant', options[this.active].id);
      else this.input.removeAttribute('aria-activedescendant');
    }
  }
  customElements.define('nb-predictive-search', NbPredictiveSearch);

  /* ---------------------------------------------------------
     Tabs accesibles
     --------------------------------------------------------- */
  class NbTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = $$('[role="tab"]', this);
      this.tabs.forEach((tab, i) => {
        const warm = () => {
          const panel = document.getElementById(tab.getAttribute('aria-controls'));
          const rail = panel && $('nb-rail', panel);
          rail && rail.loadImages && rail.loadImages(2);
        };
        tab.addEventListener('pointerenter', warm, { once: true });
        tab.addEventListener('touchstart', warm, { once: true, passive: true });
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
        if (on && panel) {
          $$('.is-pending', panel).forEach((el) => el.classList.remove('is-pending'));
          const rail = $('nb-rail', panel);
          if (rail && rail.update) {
            rail.loadImages();
            rail.update();
          }
        }
      });
    }
  }
  customElements.define('nb-tabs', NbTabs);

  /* ---------------------------------------------------------
     Rails horizontales con progreso
     --------------------------------------------------------- */
  /*
    Los navegadores no cargan imágenes lazy que están fuera de pantalla dentro de un
    contenedor con scroll horizontal hasta que se deslizan a la vista. Para que al deslizar
    no aparezcan huecos, cuando el rail se acerca al viewport sus imágenes pasan a eager.
  */
  const railObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.loadImages();
          railObserver.unobserve(entry.target);
        });
      }, { rootMargin: '600px 0px' })
    : null;

  class NbRail extends HTMLElement {
    connectedCallback() {
      this.rail = $('[data-rail]', this) || $('.nb-rail', this);
      this.bar = $('[data-rail-progress]', this);
      if (!this.rail) return;
      this.update = this.update.bind(this);
      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { this.update(); ticking = false; });
      };
      this.rail.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      if (railObserver && !this.closest('[hidden]')) railObserver.observe(this);
      const prev = $('[data-rail-prev]', this);
      const next = $('[data-rail-next]', this);
      prev && prev.addEventListener('click', () => this.step(-1));
      next && next.addEventListener('click', () => this.step(1));
      this.update();
    }
    loadImages(limit) {
      const imgs = $$('img[loading="lazy"]', this.rail).filter((img) => img.offsetParent !== null || img.closest('.nb-card__media'));
      (limit ? imgs.slice(0, limit * 2) : imgs).forEach((img) => {
        if (getComputedStyle(img).display === 'none') return;
        img.loading = 'eager';
      });
    }
    step(dir) {
      const item = this.rail.firstElementChild;
      const width = item ? item.getBoundingClientRect().width : this.rail.clientWidth * 0.8;
      this.rail.scrollBy({ left: dir * (width + 16), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }
    update() {
      if (!this.bar || !this.rail) return;
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      const size = Math.min(100, (this.rail.clientWidth / Math.max(this.rail.scrollWidth, 1)) * 100);
      const progress = max > 0 ? (this.rail.scrollLeft / max) * (100 - size) : 0;
      this.bar.style.setProperty('--progress-size', `${size}%`);
      this.bar.style.width = `${size}%`;
      this.bar.style.transform = `translateX(${(progress / size) * 100}%)`;
      this.bar.parentElement.hidden = max <= 2;
    }
  }
  customElements.define('nb-rail', NbRail);

  /* ---------------------------------------------------------
     Recomendaciones de producto: se piden al acercarse al final de la página
     --------------------------------------------------------- */
  class NbRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.children.length || !this.dataset.url) return;
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('nb-recommendations');
          if (fresh && fresh.innerHTML.trim()) {
            this.innerHTML = fresh.innerHTML;
            initReveal(this);
          }
        } catch (err) {
          console.error(err);
        }
      };
      if (!('IntersectionObserver' in window)) return load();
      const io = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        load();
      }, { rootMargin: '800px 0px' });
      io.observe(this);
    }
  }
  customElements.define('nb-recommendations', NbRecommendations);

  /* ---------------------------------------------------------
     Video: autoplay silencioso con pausa accesible
     --------------------------------------------------------- */
  class NbVideo extends HTMLElement {
    connectedCallback() {
      this.video = $('video', this);
      this.toggle = $('[data-video-toggle]', this);
      if (!this.video) return;
      this.video.muted = true;
      this.video.playsInline = true;
      this.userPaused = reducedMotion.matches;
      this.toggle && this.toggle.addEventListener('click', () => {
        this.userPaused = !this.video.paused;
        this.userPaused ? this.video.pause() : this.video.play();
      });
      this.video.addEventListener('play', () => this.sync());
      this.video.addEventListener('pause', () => this.sync());
      const io = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && !this.userPaused) this.video.play().catch(() => {});
        else if (!entry.isIntersecting) this.video.pause();
      }, { threshold: 0.25 });
      io.observe(this);
      this.sync();
    }
    sync() {
      this.toggle && this.toggle.setAttribute('aria-pressed', String(this.video.paused));
    }
  }
  customElements.define('nb-video', NbVideo);

  /* ---------------------------------------------------------
     Marquesina: se pausa fuera de pantalla (ahorra batería)
     --------------------------------------------------------- */
  function initMarquees(root = document) {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.target.classList.toggle('is-offscreen', !e.isIntersecting));
    });
    $$('.nb-marquee', root).forEach((el) => io.observe(el));
  }

  /* ---------------------------------------------------------
     Reveal al entrar en pantalla
     Solo anima lo que está debajo del pliegue al cargar. Lo visible al cargar,
     lo que vive en rails horizontales y lo que está oculto nunca se esconde.
     --------------------------------------------------------- */
  function initReveal(root = document) {
    if (!('IntersectionObserver' in window) || reducedMotion.matches) return;
    const fold = window.innerHeight;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.remove('is-pending');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.01 });
    $$('[data-reveal]', root).forEach((el) => {
      if (el.closest('.nb-rail, [hidden], .nb-drawer')) return;
      const rect = el.getBoundingClientRect();
      if (rect.height === 0 || rect.top < fold) return;
      el.classList.add('is-pending');
      io.observe(el);
    });
  }
  nb.initReveal = initReveal;

  /* ---------------------------------------------------------
     Utilidades
     --------------------------------------------------------- */
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  }

  nb.formatMoney = (cents) => {
    const format = nb.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const withCommas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const noDecimals = withCommas;
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals)
      .replace(/\{\{\s*amount\s*\}\}/, dec === '00' ? withCommas : `${withCommas}.${dec}`)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${int.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`);
  };

  window.nb = nb;

  const ready = () => {
    initReveal();
    initMarquees();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();

  // Editor del theme: re-inicializa al recargar secciones
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));
})();
